import React, { useState } from 'react';
import { Clock, Plus, Trash2, X, Check } from 'lucide-react';
import timetableService from '../../services/TimetableService.js';

const BellScheduleManager = ({ show, onClose, currentSchedule, onScheduleSaved }) => {
  const [scheduleName, setScheduleName] = useState(currentSchedule?.name || 'Regular School Day');
  const [slots, setSlots] = useState(currentSchedule?.slots || [
    { slotOrder: 1, label: 'Period 1', startTime: '09:00', endTime: '09:45', slotType: 'TEACHING' },
    { slotOrder: 2, label: 'Period 2', startTime: '09:45', endTime: '10:30', slotType: 'TEACHING' },
    { slotOrder: 3, label: 'Short Break', startTime: '10:30', endTime: '10:45', slotType: 'SHORT_BREAK' },
    { slotOrder: 4, label: 'Period 3', startTime: '10:45', endTime: '11:30', slotType: 'TEACHING' },
    { slotOrder: 5, label: 'Period 4', startTime: '11:30', endTime: '12:15', slotType: 'TEACHING' },
    { slotOrder: 6, label: 'Lunch Break', startTime: '12:15', endTime: '13:00', slotType: 'LUNCH' },
    { slotOrder: 7, label: 'Period 5', startTime: '13:00', endTime: '13:45', slotType: 'TEACHING' },
    { slotOrder: 8, label: 'Period 6', startTime: '13:45', endTime: '14:30', slotType: 'TEACHING' },
    { slotOrder: 9, label: 'Period 7', startTime: '14:30', endTime: '15:15', slotType: 'TEACHING' },
    { slotOrder: 10, label: 'Period 8', startTime: '15:15', endTime: '16:00', slotType: 'TEACHING' },
  ]);
  const [saving, setSaving] = useState(false);

  if (!show) return null;

  const handleAddSlot = () => {
    const nextOrder = slots.length + 1;
    const teachingCount = slots.filter(s => s.slotType === 'TEACHING').length + 1;
    setSlots([
      ...slots,
      { slotOrder: nextOrder, label: `Period ${teachingCount}`, startTime: '16:00', endTime: '16:45', slotType: 'TEACHING' }
    ]);
  };

  const handleRemoveSlot = (index) => {
    setSlots(slots.filter((_, idx) => idx !== index));
  };

  const handleSlotChange = (index, field, value) => {
    const updated = [...slots];
    updated[index][field] = value;

    // Automatic Smart Label Suggestion to prevent type/label contradictions
    if (field === 'slotType') {
      if (value === 'LUNCH') {
        updated[index]['label'] = 'Lunch Break';
      } else if (value === 'SHORT_BREAK') {
        updated[index]['label'] = 'Short Break';
      } else if (value === 'TEACHING' && (updated[index]['label'] === 'Lunch Break' || updated[index]['label'] === 'Short Break')) {
        const teachingBefore = updated.slice(0, index).filter(s => s.slotType === 'TEACHING').length + 1;
        updated[index]['label'] = `Period ${teachingBefore}`;
      }
    }

    setSlots(updated);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const res = await timetableService.saveBellSchedule({
        name: scheduleName,
        academicYear: '2026-2027',
        slots: slots
      });
      if (onScheduleSaved) {
        onScheduleSaved(res.schedule || { name: scheduleName, slots });
      }
      onClose();
    } catch (err) {
      console.error('Failed to save bell schedule:', err);
      alert('Failed to save bell schedule: ' + (err.message || 'Error occurred'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fs-modal-overlay" style={{ zIndex: 1100 }}>
      <div className="fs-modal-card" style={{ maxWidth: '640px' }}>
        <div className="fs-header" style={{ padding: '20px 24px', borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#0F172A', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Clock size={20} color="#2563EB" /> Configure School Bell Schedule
            </h3>
            <div style={{ fontSize: '12.5px', color: '#64748B', marginTop: '2px' }}>
              Define daily period time boundaries, tea breaks, and lunch slots.
            </div>
          </div>
          <button className="fs-close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="fs-body" style={{ padding: '24px', maxHeight: '60vh', overflowY: 'auto' }}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ fontSize: '12.5px', fontWeight: '700', color: '#334155', marginBottom: '6px', display: 'block' }}>
              Schedule Name *
            </label>
            <input 
              type="text" 
              className="form-control"
              value={scheduleName}
              onChange={(e) => setScheduleName(e.target.value)}
              style={{ height: '42px', borderRadius: '8px', width: '100%', fontSize: '14px' }}
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
            <span style={{ fontSize: '13px', fontWeight: '700', color: '#0F172A' }}>Periods & Break Slots ({slots.length})</span>
            <button 
              type="button" 
              onClick={handleAddSlot}
              style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', color: '#2563EB', padding: '6px 12px', borderRadius: '6px', fontSize: '12.5px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
            >
              <Plus size={15} /> Add Period
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {slots.map((slot, idx) => (
              <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr 1fr 40px', gap: '10px', alignItems: 'center', padding: '10px', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '8px' }}>
                <input 
                  type="text" 
                  value={slot.label} 
                  onChange={(e) => handleSlotChange(idx, 'label', e.target.value)} 
                  placeholder="Label"
                  style={{ height: '36px', borderRadius: '6px', border: '1px solid #CBD5E1', padding: '0 10px', fontSize: '13px' }}
                />
                <input 
                  type="time" 
                  value={slot.startTime} 
                  onChange={(e) => handleSlotChange(idx, 'startTime', e.target.value)} 
                  style={{ height: '36px', borderRadius: '6px', border: '1px solid #CBD5E1', padding: '0 8px', fontSize: '13px' }}
                />
                <input 
                  type="time" 
                  value={slot.endTime} 
                  onChange={(e) => handleSlotChange(idx, 'endTime', e.target.value)} 
                  style={{ height: '36px', borderRadius: '6px', border: '1px solid #CBD5E1', padding: '0 8px', fontSize: '13px' }}
                />
                <select 
                  value={slot.slotType} 
                  onChange={(e) => handleSlotChange(idx, 'slotType', e.target.value)}
                  style={{ height: '36px', borderRadius: '6px', border: '1px solid #CBD5E1', padding: '0 8px', fontSize: '12.5px' }}
                >
                  <option value="TEACHING">Teaching</option>
                  <option value="SHORT_BREAK">Short Break</option>
                  <option value="LUNCH">Lunch Break</option>
                </select>
                <button 
                  type="button" 
                  onClick={() => handleRemoveSlot(idx)}
                  style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', color: '#DC2626', width: '36px', height: '36px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="fs-footer" style={{ padding: '16px 24px', borderTop: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', backgroundColor: '#F8FAFC' }}>
          <button type="button" className="fs-btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="fs-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : '✓ Save Bell Schedule'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BellScheduleManager;
